import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Popover,
  CircularProgress,
  IconButton
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { DateRange } from "react-date-range";
import { enUS } from "date-fns/locale";
import { format } from "date-fns";
import axiosInstance from "../axios";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";

function BusinessGraphs() {
  const { businessId } = useParams();
  const [isloading, setIsLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [selectedScansDateRange, setSelectedScansDateRange] = useState(null);
  const today = new Date();
  const priorDate = new Date();
  priorDate.setDate(today.getDate() - 29);
  const [scansdateRange, setsScansDateRange] = useState([
    {
      startDate: priorDate,
      endDate: today,
      key: "selection",
    },
  ]);
  const [clicksData, setClicksData] = useState([]);
  const [anchorElClicks, setAnchorElClicks] = useState(null); // for Clicks
  const [anchorElScans, setAnchorElScans] = useState(null); // for Scans
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [isDateRangeLoading, setIsDateRangeLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: priorDate,
      endDate: today,
      key: "selection",
    },
  ]);
  const [isScansDateInvalid, setIsScansDateInvalid] = useState(false);
  const [isClicksDateInvalid, setIsClicksDateInvalid] = useState(false);
  const navigate =useNavigate();
  const location = useLocation();
  const { name, address } = location.state || {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const providerColorMap = {
    google: "#4285F4",
    instagram: "#E1306C",
    whatsapp: "#25D366",
    yelp: "#AF0606",
    facebook: "#1461C0",
    tiktok: "#010101",
    tripadvisor: "#34E0A1",
    customprovider_8: "#FF9800",
    customprovider_9: "#9C27B0",
  };



  

  const getRandomColor = (provider) => {
    const key = provider.toLowerCase();
    return providerColorMap[key] || "#CCCCCC";
  };

  useEffect(() => {
    setIsLoading(true); // <--- Start
    Promise.all([
      fetchChartData(priorDate, today),
      fetchClickReports(priorDate, today),
    ]).finally(() => setIsLoading(false)); // <--- End loading after both are fetched
  }, [businessId]);

  const fetchClickReports = async (start = null, end = null) => {
    setIsDateRangeLoading(true);
    try {
      const payload = {
        business_id: businessId,
        ...(start &&
          end && {
            start_date: format(start, "yyyy-MM-dd"),
            end_date: format(end, "yyyy-MM-dd"),
          }),
      };

      const response = await axiosInstance.post(
        "orders.php?method=generate_clicks_reports",
        payload
      );

      if (response.data.clicks) {
        const chartData = response.data.clicks.map((item, index) => ({
          id: index,
          value: parseInt(item.click_count),
          name: item.provider.includes("customprovider")
            ? item.provider_label
            : item.provider_label || item.provider,
          color: getRandomColor(item.provider),
        }));
        setClicksData(chartData);
      }
    } catch (err) {
      console.log("error", err);
    }
    setIsDateRangeLoading(false);
  };

  const handleOpenCalendar = (event) => {
    setAnchorElClicks(event.currentTarget);
  };

  const handleApplyDate = () => {
    const { startDate, endDate } = dateRange[0];
    if (!endDate || startDate.getTime() === endDate.getTime()) {
      setIsClicksDateInvalid(true);
      return;
    }
    setIsClicksDateInvalid(false);
    setSelectedDateRange(dateRange[0]);
    fetchClickReports(startDate, endDate);
    setAnchorElClicks(null);
  };

  const handleReset = () => {
    setSelectedDateRange(null);
    fetchClickReports();
    setAnchorElClicks(null);
  };

  const formatChartData = (apiData) => {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    return apiData.map((item) => {
      const date = new Date(item.created_at);
      const day = date.getDate();
      const month = monthNames[date.getMonth()];

      return {
        name: `${month}, ${day}`,
        Scans: Number(item.click),
      };
    });
  };

  const fetchChartData = async (start = null, end = null) => {
    setIsDateRangeLoading(true);
    try {
      const payload = {
        business_id: businessId,
        ...(start &&
          end && {
            start_date: format(start, "yyyy-MM-dd"),
            end_date: format(end, "yyyy-MM-dd"),
          }),
      };

      const response = await axiosInstance.post(
        "orders.php?method=generate_scans_reports",
        payload
      );
      const transformedData = formatChartData(response.data.clicks);
      setChartData(transformedData);
    } catch (error) {
      console.error("Error fetching chart data", error);
    }
    setIsDateRangeLoading(false);
  };

  const handleScansOpenCalender = (event) => {
    setAnchorElScans(event.currentTarget);
  };

  const handleApplyScansDate = () => {
    const { startDate, endDate } = scansdateRange[0];
    if (!endDate || startDate.getTime() === endDate.getTime()) {
      setIsScansDateInvalid(true);
      return;
    }
    setIsScansDateInvalid(false);
    setSelectedScansDateRange(scansdateRange[0]);
    fetchChartData(startDate, endDate);
    setAnchorElScans(null);
  };

  const handleScansReset = () => {
    setSelectedScansDateRange(null);
    fetchChartData();
    setAnchorElScans(null);
  };

  const handleNavigateBack =()=>
    {
     navigate('/dashboard/businessreports');
    }

  const CustomTool = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const total = clicksData.reduce((sum, item) => sum + item.value, 0);
      const percent = ((payload[0].value / total) * 100).toFixed(1);
      return (
        <Box
          sx={{
            backgroundColor: "var(--pg-card)",
            py: "4px",
            px: "10px",
            border: "1px solid #ccc",
            display: "flex",
          }}
        >
          <Typography
            sx={{
              pr: "8px",
              fontSize: "14px",
              fontfamily: "Roboto",
              fontWeight: "500",
            }}
          >
            {payload[0].name}
          </Typography>
          <Typography
            sx={{ fontSize: "14px", fontfamily: "Roboto", fontWeight: "500" }}
          >
            {payload[0].value} {t("Clicks")}
            {/* ({percent}%) */}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            background: "var(--pg-card)",
            padding: "5px 10px",
            borderRadius: "5px",
            boxShadow: 2,
          }}
        >
          <Typography fontSize={12} color="#444">
            🔵 {t("Scans")}: <strong>{payload[0].value}</strong>
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (isloading) {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh", // Full viewport height
          }}
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (!businessId) {
    return (
      <>
        <Typography
          sx={{
            textAlign: "center",
            pt: "50px",
            fontFamily: "Nunito",
            fontSize: "20px",
            fontWeight: "600",
          }}
        >
          No businesses available at the moment.
        </Typography>
      </>
    );
  }

  return (
    <>
      {/* SCANS Overview */}
     <Box
         sx={{
           display: "flex",
           flexDirection: "column",
           px: { xs: "10px", sm: "5px" },
           mb: 2,
         }}
       >
         {/* Top Row for Mobile: Empty space + Back Button */}
         {isMobile && (
           <Box
             sx={{
               display: "flex",
               justifyContent: "space-between",
               alignItems: "center",
               mb: 1,
             }}
           >
             <Box /> {/* empty space */}
             <IconButton onClick={handleNavigateBack} sx={{ color: "primary.main" }}>
               <ChevronLeft sx={{ color: "primary.main" }} />
               <Typography sx={{ fontWeight: "bold", color: "primary.main" }}>
               {t("Back")}
               </Typography>
             </IconButton>
           </Box>
         )}
     
         {/* Title and Name */}
         <Box
           sx={{
             display: "flex",
             alignItems: "center",
             justifyContent: isMobile ? "flex-start" : "space-between",
           }}
         >
           <Box sx={{ display: "flex", alignItems: "center" }}>
             <Typography
               sx={{
                 fontFamily: "Roboto",
                 fontSize: { xs: "18px", sm: "28px" },
                 fontWeight: "600",
                 lineHeight: { xs: "24px", sm: "16px" },
                 color: "var(--pg-text)",
                 pr: "4px",
               }}
             >
              {t("Business Graphs")}
             </Typography>
             <Typography
               sx={{
                 color: "var(--pg-text)",
                 fontFamily: "Roboto",
                 lineHeight: { xs: "23px", sm: "20px" },
                 fontSize: { xs: "18px", sm: "20px" },
                 pr: "8px",
                 fontWeight: 600,
                 pl: "4px",
               }}
             >
               -
             </Typography>
             <Typography
               sx={{
                 color: "var(--pg-text)",
                 fontFamily: "Roboto",
                 lineHeight: { xs: "23px", sm: "20px" },
                 fontSize: { xs: "16px", sm: "20px" },
                 fontWeight: 600,
               }}
             >
               {name}
             </Typography>
           </Box>
     
           {/* Back button for Desktop/Tablet */}
           {!isMobile && (
             <IconButton onClick={handleNavigateBack} sx={{ color: "primary.main" }}>
               <ChevronLeft sx={{ color: "primary.main" }} />
               <Typography sx={{ fontWeight: "bold", color: "primary.main" }}>
               {t("Back")}
               </Typography>
             </IconButton>
           )}
         </Box>
     
         {/* Address */}
         <Box
           sx={{
             display: "flex",
             justifyContent: "space-between",
             alignItems: "center",
             mt: 1,
           }}
         >
           <Typography
             sx={{
               color: "var(--pg-text-muted)",
               fontFamily: "Roboto",
               fontSize: "16px",
               fontWeight: 400,
             }}
           >
             {address}
           </Typography>
     
           {/* On mobile, no back button here */}
         </Box>
         </Box>
      <Box
        sx={{
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
          p: 2,
          background: "var(--pg-card)",
          mt: "10px",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2.5}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "700",
              fontFamily: "Roboto",
              color: "var(--pg-text)",
            }}
          >
           {t("Scans Overview")}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              onClick={handleScansOpenCalender}
              endIcon={<CalendarMonthIcon />}
              variant="outlined"
              size="small"
            >
              {selectedScansDateRange
                ? `${format(
                    selectedScansDateRange.startDate,
                    "MMM dd"
                  )} - ${format(selectedScansDateRange.endDate, "MMM dd")}`
                :  t("Filter by")}
            </Button>
            <Button onClick={handleScansReset} variant="text" size="small">
            {t("Reset")}
            </Button>
          </Stack>
        </Box>

        {/* Scans Popover */}
        <Popover
          open={Boolean(anchorElScans)}
          anchorEl={anchorElScans}
          onClose={() => setAnchorElScans(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Box p={2}>
            <DateRange
              editableDateInputs
              onChange={(item) => setsScansDateRange([item.selection])}
              moveRangeOnFirstSelection={false}
              ranges={scansdateRange}
              maxDate={new Date()}
              locale={enUS}
              className={isScansDateInvalid ? "invalid-date-range" : ""}
            />
            <Stack direction="row" justifyContent="flex-end" spacing={1} mt={1}>
              <Button
                size="small"
                onClick={handleApplyScansDate}
                variant="contained"
              >
                {t("Apply")}
              </Button>
            </Stack>
          </Box>
        </Popover>

        {/* Scans Chart */}
        <ResponsiveContainer width="100%" height={250}>
          {chartData?.length > 0 ? (
            <BarChart data={chartData} barSize={24}>
              <CartesianGrid stroke="#E5E5E5" vertical={false} />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={{ stroke: "#E5E5E5" }}
                tick={{
                  fontSize: "12px",
                  fontFamily: "Roboto",
                  fill: "#8F9BB3",
                }}
              />
              <YAxis
                tickFormatter={(value) => `${value}`}
                tickLine={false}
                axisLine={false}
                domain={[0, "dataMax"]}
                allowDecimals={false}
                width={30}
                tick={{
                  fontSize: "12px",
                  fontFamily: "Roboto",
                  fill: "#8F9BB3",
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="bottom"
                iconType="circle"
                wrapperStyle={{ fontSize: "11px", fontFamily: "Roboto" }}
              />
              <Bar dataKey="Scans" fill="#54AEF8" radius={[4, 4, 0, 0]} />
            </BarChart>
          ) : (
            <Typography
              sx={{
                fontSize: "14px",
                fontFamily: "Roboto",
                textAlign: "center",
                pt: 2,
              }}
            >
              {t("No Scans Available")}
            </Typography>
          )}
        </ResponsiveContainer>
      </Box>

      {/* CLICKS Overview */}
      <Box
        height={300}
        sx={{
          backgroundColor: "var(--pg-card)",
          mt: "10px",
          mb: "4px",
          border: "solid 1px  #E0E0E0",
          borderRadius: "6px",
          position: "relative",
          px: 2,
          pt: 1,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            sx={{ fontSize: "16px", fontWeight: "600", fontFamily: "Roboto" }}
          >
            {t("Clicks Overview")}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              onClick={handleOpenCalendar}
              endIcon={<CalendarMonthIcon />}
              variant="outlined"
              size="small"
            >
              {selectedDateRange
                ? `${format(selectedDateRange.startDate, "MMM dd")} - ${format(
                    selectedDateRange.endDate,
                    "MMM dd"
                  )}`
                : t("Filter by")}
            </Button>
            <Button onClick={handleReset} variant="text" size="small">
            {t("Reset")}
            </Button>
          </Stack>
        </Box>

        {/* Clicks Popover */}
        <Popover
          open={Boolean(anchorElClicks)}
          anchorEl={anchorElClicks}
          onClose={() => setAnchorElClicks(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Box p={2}>
            <DateRange
              editableDateInputs
              onChange={(item) => setDateRange([item.selection])}
              moveRangeOnFirstSelection={false}
              ranges={dateRange}
              maxDate={new Date()}
              locale={enUS}
              className={isClicksDateInvalid ? "invalid-date-range" : ""}
            />
            <Stack direction="row" justifyContent="flex-end" spacing={1} mt={1}>
              <Button
                size="small"
                onClick={handleApplyDate}
                variant="contained"
              >
                {t("Apply")}
              </Button>
            </Stack>
          </Box>
        </Popover>

        {/* Clicks Chart */}
        {clicksData?.length > 0 ? (
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={clicksData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="42%"
                outerRadius={100}
                innerRadius={36}
              >
                {clicksData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTool />} />
              <Legend verticalAlign="middle" align="right" layout="vertical" />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <Typography
            sx={{
              fontSize: "14px",
              fontFamily: "Roboto",
              textAlign: "center",
              pt: 2,
            }}
          >
            {t("No Clicks Available")}
          </Typography>
        )}
      </Box>

      {/* Loader */}
      {isDateRangeLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255,255,255,0.6)",
            backdropFilter: "blur(1px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}
    </>
  );
}

export default BusinessGraphs;